// src/oura/components/newscan/DraftBuilding.tsx
//
// What the Review step shows while the 50 are being written (a job: two AI
// calls, 30 to 120 seconds live). Left-aligned, read top to bottom (Jovi, 20 Oct:
// the centred grey list said nothing about how far along it was):
//   - the title with the business, and "Step 3 of 6 · 0:24 · usually under 2 minutes";
//   - a progress bar by job step;
//   - the steps done so far, the one running in bold, the rest folded into one line;
//   - "Drafting from": the outlets and services the 50 are written from, so a
//     wrong one ("Lush") can be spotted before the 50 exist;
//   - the line that nothing has been spent and the page can be left, with
//     "Check the business" (the Business step reads fine while it drafts).
// When the job fails: its own sentence and Try again.
//
// The current step is said once through a polite live region, so a screen
// reader hears the job move without the list being read again.
//
// Brand mode (v4.5, SPEC-v45 §6) runs its own six steps: no outlet check, and
// "Pulling Google searches for <markets>" after reading the site.

import { useEffect, useState } from "react";
import { ArrowRightIcon, CheckIcon, Loader2Icon } from "lucide-react";
import type { AnyScanDraftJobStep, ScanDraft } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { PillButton, Surface, TYPE } from "@oura/ui";
import { Refusal } from "@oura/components/onboarding/fields";
import { isBrandBuild, pulledMarkets } from "./brandReview";

// The job's last two steps (filling, then checking) read as one: "Writing 50 questions" (Jovi, 20 Oct).
const LOCAL_STEPS: AnyScanDraftJobStep[] = ["reading_site", "checking_outlets", "researching", "writing_words", "filling"];
const BRAND_STEPS: AnyScanDraftJobStep[] = ["reading_site", "pulling_demand", "researching", "writing_words", "filling"];

const COPY = {
    title: (total: number) => `Drafting the ${total}`,
    titleFor: (total: number, name: string) => `Drafting the ${total} for ${name}`,
    progress: (n: number, of: number) => `Step ${n} of ${of}`,
    usual: "usually under 2 minutes",
    then: (rest: string[]) => `Then: ${rest.map((r) => r.replace(/^./, (c) => c.toLowerCase())).join(", ")}`,
    from: "Drafting from",
    checkBusiness: "Check the business",
    failedTitle: (total: number) => `The ${total} could not be drafted`,
    step: (step: AnyScanDraftJobStep, recipe: string, total: number): string =>
        ({
            reading_site: "Reading the site",
            checking_outlets: "Checking the outlets",
            pulling_demand: "Pulling Google searches",
            researching: "Reading reviews and Google searches",
            writing_words: "Choosing the words customers type",
            filling: `Writing ${total} questions`,
            checking: `Writing ${total} questions`,
        })[step],
    /** Brand mode words where they differ from the local ones. */
    brandStep: (step: AnyScanDraftJobStep, markets: string): string | null =>
        step === "pulling_demand"
            ? markets
                ? `Pulling Google searches for ${markets}`
                : "Pulling Google searches"
            : step === "researching"
              ? "Reading what buyers ask"
              : step === "writing_words"
                ? "Picking the searches to ask"
                : null,
    leave: "Nothing spent yet: no engines are asked until you press Start. You can leave this page; the draft keeps building.",
    failedFallback: "The draft stopped before the questions were written.",
    retry: "Try again",
    nothingSpent: "No engines asked. Trying again costs cents.",
    done: "done",
    now: "in progress",
} as const;

export interface DraftBuildingProps {
    draft: ScanDraft;
    /** Try again after a failed job (the parent drafts again, or starts a fresh draft). */
    onRetry(): void;
    retrying?: boolean;
    /** Opens the Business step, which reads fine while the 50 are drafted. Omit to hide the button. */
    onCheckBusiness?: () => void;
}

/** "0:24" since the job started; null when the job has no start time yet. */
function useElapsed(startedAt: string | null | undefined): string | null {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!startedAt) return;
        const t = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, [startedAt]);
    const at = startedAt ? Date.parse(startedAt) : NaN;
    if (Number.isNaN(at)) return null;
    const secs = Math.max(0, Math.floor((now - at) / 1000));
    return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
}

export function DraftBuilding({ draft, onRetry, retrying, onCheckBusiness }: DraftBuildingProps) {
    const total = draft.counts.total || 50;
    const failed = draft.status === "failed" || draft.job?.status === "failed";
    const brand = isBrandBuild(draft);
    const steps = brand ? BRAND_STEPS : LOCAL_STEPS;
    const markets = brand ? pulledMarkets(draft) : "";
    // Read wide: the contract's job step joins the brand one with the brand fixtures.
    const jobStep = (draft.job?.step ?? null) as AnyScanDraftJobStep | null;
    // A step this list does not have (an older job) reads as the first one, never as none.
    // "checking" is shown as part of writing the questions.
    const shownStep = jobStep === "checking" ? "filling" : jobStep;
    const at = shownStep ? Math.max(0, steps.indexOf(shownStep)) : 0;
    // A recipe update is written to the new recipe, whichever mode is picked later.
    const recipe = draft.kind === "recipe_change" ? (draft.recipe.proposedLabel ?? draft.recipe.label) : draft.recipe.label;
    const label = (step: AnyScanDraftJobStep) => (brand && COPY.brandStep(step, markets)) || COPY.step(step, recipe, total);
    const elapsed = useElapsed(failed ? null : draft.job?.startedAt);

    if (failed) {
        return (
            <Surface className="items-center px-6 py-10 text-center sm:py-12">
                <h1 data-step-title="" tabIndex={-1} className={cn(TYPE.tt, "m-0 text-gray-900 outline-none")}>
                    {COPY.failedTitle(total)}
                </h1>
                <Refusal className="mt-4 max-w-md text-left">{draft.job?.error || COPY.failedFallback}</Refusal>
                <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>{COPY.nothingSpent}</p>
                <PillButton variant="primary" busy={retrying} onClick={onRetry} className="mt-5 max-sm:h-9">
                    {COPY.retry}
                </PillButton>
            </Surface>
        );
    }

    const nowLabel = label(steps[at]);
    const name = draft.business.businessName.trim();
    const rest = steps.slice(at + 1).map(label);
    // Half a step for the one running, so the bar moves as soon as the job starts.
    const pct = Math.round(((at + 0.5) / steps.length) * 100);
    const from = [...draft.business.outlets.map((o) => o.name.trim()), ...draft.business.services.map((x) => x.name.trim())].filter(Boolean);
    const meta = [COPY.progress(at + 1, steps.length), elapsed, COPY.usual].filter(Boolean).join(" · ");

    return (
        <div className="space-y-5">
            <div className="space-y-1">
                <h1 data-step-title="" tabIndex={-1} className={cn(TYPE.h1, "m-0 text-gray-900 outline-none")}>
                    {name ? COPY.titleFor(total, name) : COPY.title(total)}
                </h1>
                <p className={cn(TYPE.ts, "m-0 tabular-nums")}>{meta}</p>
            </div>

            <div
                role="progressbar"
                aria-label={COPY.title(total)}
                aria-valuemin={0}
                aria-valuemax={steps.length}
                aria-valuenow={at}
                aria-valuetext={nowLabel}
                className="h-1.5 overflow-hidden rounded-full bg-gray-200"
            >
                <div className="h-full rounded-full bg-blue-600 motion-safe:transition-[width] motion-safe:duration-700" style={{ width: `${pct}%` }} />
            </div>

            <Surface className="gap-4">
                <ol aria-label={COPY.title(total)} className="list-plain m-0 space-y-2.5 p-0">
                    {steps.slice(0, at + 1).map((step, i) => {
                        const now = i === at;
                        return (
                            <li key={step} aria-current={now ? "step" : undefined} className={cn(TYPE.tb, "flex items-center gap-2.5 text-gray-900", now && "font-semibold")}>
                                <span aria-hidden className="grid h-4 w-4 shrink-0 place-items-center">
                                    {now ? (
                                        <Loader2Icon size={14} className="text-blue-600 motion-safe:animate-spin" />
                                    ) : (
                                        <CheckIcon size={14} strokeWidth={2.5} className="text-emerald-600" />
                                    )}
                                </span>
                                <span className="min-w-0">
                                    {label(step)}
                                    <span className="sr-only">, {now ? COPY.now : COPY.done}</span>
                                </span>
                            </li>
                        );
                    })}
                    {rest.length > 0 && (
                        <li className={cn(TYPE.tc, "flex items-start gap-2.5 text-gray-500")}>
                            <span aria-hidden className="grid h-[18px] w-4 shrink-0 place-items-center">
                                <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                            </span>
                            <span className="min-w-0">{COPY.then(rest)}</span>
                        </li>
                    )}
                </ol>

                {from.length > 0 && (
                    <div className="space-y-2 border-t border-gray-100 pt-4">
                        <p className={cn(TYPE.lab, "m-0")}>{COPY.from}</p>
                        <ul className="list-plain m-0 flex flex-wrap gap-1.5 p-0">
                            {from.map((f, i) => (
                                <li key={`${f}-${i}`} className={cn(TYPE.tc, "rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700")}>
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Surface>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <p className={cn(TYPE.tc, "m-0 min-w-0 flex-1 text-gray-500")}>{COPY.leave}</p>
                {onCheckBusiness && (
                    <PillButton size="sm" icon={ArrowRightIcon} onClick={onCheckBusiness}>
                        {COPY.checkBusiness}
                    </PillButton>
                )}
            </div>
            <p aria-live="polite" className="sr-only">
                {nowLabel}
            </p>
        </div>
    );
}
